import { Controller } from "@hotwired/stimulus"

// Auto-submits a form via fetch PATCH con Accept: application/json.
// Uso pensado para los edit forms del editor visual de paginas, donde
// no hay boton "Guardar": cada cambio persiste directo y el iframe
// preview se actualiza via broadcast server-side (PreviewBroadcastable).
//
// Eventos esperados en el form:
//   change -> submitNow          (selects, radios, checkboxes → instantaneo)
//   input  -> submitDebounced    (text fields/textareas → debounce 400ms)
//
// Response JSON no re-renderiza el frame, asi el focus se preserva. Por eso
// los errores de validacion se pintan desde aca: el servidor responde 422 con
// `{ errors: { campo: [mensaje] } }` y montamos el mismo markup que
// `form_field` para que el usuario vea QUE campo fallo y por que. Sin esto el
// guardado falla en silencio y el valor vuelve al anterior al recargar.
//
// Al success dispara `editor:record-saved` para que el draft-store
// marque dirty y habilite "Guardar"/"Descartar".
export default class extends Controller {
  static values = { delay: { type: Number, default: 400 } }

  // Mismo markup que FormHelper::ERROR_CLASSES — si cambia alla, cambia aca.
  static ERROR_CLASSES = "text-[#ff2f37] text-[11px] my-[5px] leading-[1.5]"
  static ERROR_FLAG = "data-auto-submit-error"

  disconnect() {
    clearTimeout(this._timeout)
  }

  submitNow() {
    clearTimeout(this._timeout)
    this.#submit()
  }

  submitDebounced() {
    clearTimeout(this._timeout)
    this._timeout = setTimeout(() => this.#submit(), this.delayValue)
  }

  async #submit() {
    const form = this.element
    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    const response = await fetch(form.action, {
      method: "POST",
      headers: { "Accept": "application/json", "X-CSRF-Token": csrf },
      body: new FormData(form)
    })

    this.#clearErrors()

    if (response.ok) {
      document.dispatchEvent(new CustomEvent("editor:record-saved", { bubbles: true }))
      return
    }

    const payload = await response.json().catch(() => ({}))
    this.#renderErrors(payload.errors)
  }

  #clearErrors() {
    this.element
      .querySelectorAll(`[${this.constructor.ERROR_FLAG}]`)
      .forEach((node) => node.remove())
  }

  // `errors` viene como { campo: [mensaje, ...] }. Un array plano (shape viejo)
  // no dice a que campo pertenece, asi que se muestra sobre el form completo.
  #renderErrors(errors) {
    if (!errors) return

    if (Array.isArray(errors)) {
      this.#insertError(this.element.firstElementChild, errors.join(", "))
      return
    }

    Object.entries(errors).forEach(([field, messages]) => {
      const input = this.element.querySelector(`[name$="[${field}]"]`)
      if (!input) return

      const text = Array.isArray(messages) ? messages.join(", ") : String(messages)
      this.#insertError(input.parentElement, text)
    })
  }

  #insertError(anchor, text) {
    if (!anchor) return

    const paragraph = document.createElement("p")
    paragraph.className = this.constructor.ERROR_CLASSES
    paragraph.setAttribute(this.constructor.ERROR_FLAG, "")

    const icon = document.createElement("i")
    icon.className = "fa-solid fa-circle-exclamation align-middle mr-[5px]"

    paragraph.append(icon)
    // `append` con string escapa el texto: el mensaje viene del servidor pero
    // puede incluir lo que el usuario tecleo (ej. el permalink sugerido).
    paragraph.append(text)

    anchor.insertAdjacentElement("beforebegin", paragraph)
  }
}
